import { Anchor, Breadcrumbs, Button, Card, Grid, Group, Text } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconDeviceFloppy } from '@tabler/icons-react';
import { useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router';
import ErrorMessage from '../../../components/ErrorMessage';
import LockableButton from '../../../components/Lockable/LockableButton';
import Metadata from '../../../components/Metadata';
import ReportsForm from '../../../components/Reports/ReportsForm';
import { Page } from '../../../constants/pageTitles';
import useFetch from '../../../helpers/useFetch';
import useIsValidReport from '../../../helpers/useIsValidReport';

const page = `${Page['reports']}`;

const defaultReport = {
  name: 'New report',
  type: 'oneTime',
  periodStart: null,
  periodEnd: null,
  schedule: undefined,
  scope: null,
  selection: 'all',
  elements: [],
  recipients: [],
};

const ReportsNewPage = () => {
  const { accountId, domainId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const fetch = useFetch();
  const { user } = useSelector((state) => state.auth);
  const [report, setReport] = useState({ ...defaultReport, recipients: [user.email] });
  const [userError, setUserError] = useState();
  const isValidReport = useIsValidReport(report);

  const handleChange = (event) => {
    const { name, value } = event.target;

    setReport((prevState) => {
      const newState = { ...prevState };

      if (name === 'type' && value === 'oneTime') {
        newState.schedule = null;
      }

      if (name === 'type' && value === 'periodic') {
        newState.periodStart = null;
        newState.periodEnd = null;
      }

      if (name === 'scope') {
        newState.elements = [];
      }

      return { ...newState, [name]: value };
    });
  };

  const createReport = (report) => {
    fetch(`/api/admin/accounts/${accountId}/domains/${domainId}/exports`, {
      method: 'POST',
      body: JSON.stringify(report),
    })
      .then(() => {
        notifications.show({
          title: 'Export Scheduled',
          message:
            'Your export has been scheduled for processing. You will receive an email with the export once it has been processed.',
          color: 'success',
        });
        navigate(location.pathname.replace(`/reports/new`, '/reports'));
      })
      .catch(setUserError);
  };

  if (!report) {
    return;
  }

  const backButton = (
    <Button variant="default" onClick={() => navigate(-1)}>
      {' '}
      Back
    </Button>
  );

  const saveButton = (
    <LockableButton
      variant="primary"
      onClick={() => createReport(report)}
      disabled={!isValidReport}
      data-tour="reportSaveBtn"
      restrictionKey="reportsCreate"
    >
      <IconDeviceFloppy size={14} /> Save
    </LockableButton>
  );

  return (
    <>
      <Metadata title={`${page} | New`} />
      <Breadcrumbs mb="md">
        <Anchor href={`#/accounts/${accountId}/domains/${domainId}/analytics`}>Home</Anchor>
        <Anchor href={`#/accounts/${accountId}/domains/${domainId}/reports`}>{page}</Anchor>
        <Text>New</Text>
      </Breadcrumbs>

      <Grid>
        <Grid.Col>
          <Card withBorder>
            <Card.Section inheritPadding py="sm">
              <Grid.Col span={12}>
                <ReportsForm report={report} handleChange={(event) => handleChange(event)} />
                <Group justify="flex-end" gap="xs" py="md">
                  {backButton}
                  {saveButton}
                </Group>
              </Grid.Col>
            </Card.Section>
          </Card>
        </Grid.Col>
      </Grid>
      {userError ? (
        <div style={{ textAlign: 'right' }}>
          <ErrorMessage message={userError.message} />
        </div>
      ) : null}
    </>
  );
};

export default ReportsNewPage;
